import { GearPal, LivePal } from "../../types/Pal";
import apiClient from "./client";

// Interfaccia per i filtri LivePal
export interface LivePalFilters {
    types?: string[];
    zipCode?: string;
    searchRadius?: number;
    off?: boolean;
}

export const getAllLivepals = async (language: string, filters?: LivePalFilters): Promise<LivePal[]> => {
    const params = new URLSearchParams();
    
    if (filters?.zipCode) params.append('zipCode', filters.zipCode);
    if (filters?.searchRadius) params.append('radius', filters.searchRadius.toString());
    if (filters?.off) params.append('off', 'true');
    
    const queryString = params.toString();
    const url = queryString ? `/livepal/get-all-livepals?${queryString}` : '/livepal/get-all-livepals';
    
    const response = await apiClient.get(url, {
        headers: {
            'Accept-Language': language
        }
    });
    return response.data;
}

export const deleteLivepal = async (id: number, language: string) => {
    const user = localStorage.getItem('user-storage')
    const parsedUser = user ? JSON.parse(user) : null
    const headers = parsedUser && parsedUser?.state?.user?.access_token ? {
        'Authorization': `Bearer ${parsedUser.state.user.access_token}`,
        'Accept-Language': language
    } : {
        'Accept-Language': language
    }
    const response = await apiClient.post('/livepal/delete-livepal', {
        id: id
    }, {
        headers: headers
    });
    return response.data
}

export const getMyLivepals = async (language: string) => {
    const user = localStorage.getItem('user-storage')
    const parsedUser = user ? JSON.parse(user) : null
    const headers = parsedUser && parsedUser?.state?.user?.access_token ? {
        'Authorization': `Bearer ${parsedUser.state.user.access_token}`,
        'Accept-Language': language
    } : {
        'Accept-Language': language
    }
    const response = await apiClient.get('/livepal/my-livepals', {
        headers: headers
    });
    return response.data.result
}

// Nuovo endpoint per ottenere tutti gli equipment
export const getAllEquipment = async (language: string) => {
    const response = await apiClient.get('/livepal/equipment', {
        headers: {
            'Accept-Language': language
        }
    });
    // Rimappa i dati per restituire category, subCategory e items (array di oggetti con id e label)
    return response.data.map((eq: any) => ({
        category: eq.category,
        subCategory: eq.subCategory,
        items: eq.items.map((item: any) => ({ id: item.id, label: item.label }))
    }));
}

// Interfaccia per i dati di creazione LivePal basata sul DTO del backend
export interface CreateLivePalData {
    name: string;
    address: string;
    zipCode: string;
    city: string;
    region: string;
    country: string;
    website?: string;
    email?: string;
    capacity: number;
    venueType: 'acoustic' | 'fullBand';
    accommodation: boolean;
    meals: boolean;
    coordinates: {
        lat: number;
        lng: number;
    };
    contacts?: Array<{
        type: string;
        value: string;
    }>;
    equipment?: number[]; // Array di ID degli equipment
}

// Interfaccia per i dati di update LivePal (tutti i campi opzionali tranne le coordinate)
export interface UpdateLivePalData {
    name?: string;
    address?: string;
    zipCode?: string;
    city?: string;
    region?: string;
    country?: string;
    website?: string;
    email?: string;
    capacity?: number;
    venueType?: 'acoustic' | 'fullBand';
    accommodation?: boolean;
    meals?: boolean;
    coordinates?: {
        lat: number;
        lng: number;
    };
    contacts?: Array<{
        type: string;
        value: string;
    }>;
    equipment?: number[]; // Array di ID degli equipment
}

// Funzione per creare un nuovo LivePal
export const addLivePal = async (data: CreateLivePalData, language: string) => {
    const user = localStorage.getItem('user-storage')
    const parsedUser = user ? JSON.parse(user) : null
    
    if (!parsedUser?.state?.user?.access_token) {
        throw new Error('Token di accesso non trovato. Effettua il login.');
    }
    
    const headers = {
        'Authorization': `Bearer ${parsedUser.state.user.access_token}`,
        'Accept-Language': language,
        'Content-Type': 'application/json'
    }
    
    const response = await apiClient.post('/livepal/add-livepal', data, {
        headers: headers
    });
    
    return response.data;
}

// Funzione per aggiornare un LivePal esistente
export const updateLivePal = async (id: number, data: UpdateLivePalData, language: string) => {
    const user = localStorage.getItem('user-storage')
    const parsedUser = user ? JSON.parse(user) : null
    
    if (!parsedUser?.state?.user?.access_token) {
        throw new Error('Token di accesso non trovato. Effettua il login.');
    }
    
    const headers = {
        'Authorization': `Bearer ${parsedUser.state.user.access_token}`,
        'Accept-Language': language,
        'Content-Type': 'application/json'
    }
    
    const response = await apiClient.patch(`/livepal/update-livepal/${id}`, data, {
        headers: headers
    });
    
    return response.data;
}
